import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  useTheme,
  alpha,
  Container
} from '@mui/material';
import {
  LocalShipping as DeliveryIcon,
  ShoppingCart as OrderIcon,
  CheckCircle as DeliveredIcon,
  Storefront as StoreIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useNavigate } from 'react-router-dom';
import ItemGraph from './ItemGraph';
import { orderManagementParams } from '../OrderManagement/orderManagementParams';
import { orderManagementQuery } from '../../Query/orderManagementQuery';
import { useGetAllDashboard } from '../../Query/DashboardQuery';
import { useMemo, useEffect } from 'react';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../theme';


// API-powered data (no mock defaults)

// Dummy data for customers
// const customersData = [
//   { id: 1, name: 'Alice Johnson', email: 'alice@example.com', phone: '9876543210', status: 'Active' },
//   { id: 2, name: 'Bob Smith', email: 'bob@example.com', phone: '8765432109', status: 'Inactive' },
//   { id: 3, name: 'Charlie Brown', email: 'charlie@example.com', phone: '7654321098', status: 'Active' },
//   { id: 4, name: 'Diana Prince', email: 'diana@example.com', phone: '6543210987', status: 'Active' },
//   { id: 5, name: 'Ethan Hunt', email: 'ethan@example.com', phone: '5432109876', status: 'Inactive' },
// ];

// --- Order Management Query Integration ---
// (Removed duplicate code and moved everything inside the Dashboard component)

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const theme = useTheme();
  const navigate = useNavigate();

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Place hook calls here, inside the component
  const { params } = orderManagementParams();
  orderManagementQuery(params);

  const [selectedFromDate, setSelectedFromDate] = useState<Dayjs | null>(() => dayjs().subtract(1, 'year'));
  const [selectedToDate, setSelectedToDate] = useState<Dayjs | null>(() => dayjs());

  const fromDate = useMemo(() => selectedFromDate?.format('YYYY-MM-DD'), [selectedFromDate]);
  const toDate = useMemo(() => selectedToDate?.format('YYYY-MM-DD'), [selectedToDate]);
  const isDateRangeValid = useMemo(() => {
    if (!selectedFromDate || !selectedToDate) return false;
    return selectedToDate.isAfter(selectedFromDate) || selectedToDate.isSame(selectedFromDate, 'day');
  }, [selectedFromDate, selectedToDate]);
  const handleResetDates = () => {
    const defaultFrom = dayjs().subtract(1, 'year');
    const defaultTo = dayjs();
    setSelectedFromDate(defaultFrom);
    setSelectedToDate(defaultTo);
    // ensure refetch with defaults
    setTimeout(() => {
      refetchDashboard();
    }, 0);
  };

  // Get merchantId from localStorage similar to other params hooks
  const merchantId = (JSON.parse(localStorage.getItem('userData') || '{}')?.userId) as string | undefined;
  const { data: dashboardData, refetch: refetchDashboard } = useGetAllDashboard({
    fromDate,
    toDate,
    // Include dashboard scoping for merchant dashboard
    dashboardType: 'merchant',
    merchantId,
  } as any);
  

// removed unused local orders table build; data comes from dashboard API

  // const { params } = useManageCustomer();
  // const isReady = Boolean(params && Object.keys(params).length > 0);
  // const memoizedParams = useMemo(() => {
  //   return isReady ? params : {};
  // }, [isReady, params]);
  // const { data: customerData, isLoading: customerLoading } = useGetAllCustomer(memoizedParams);
  

  // const { params: param } = useStoreOwnerparams();
  // const isReadys = Boolean(params && Object.keys(param).length > 0);
  // const memoizedParam = useMemo(() => {
  //   return isReady ? param : {};
  // }, [isReadys, params]);

  // const { data: merchantData, isLoading: merchantLoading } = useGetAllStoreOwnersQuery(memoizedParam);

  // Process customer data
  // const customersData = customerData?.data?.slice(0, 5)?.map((customer: any, index: number) => ({
  //   id: index + 1,
  //   name: customer.name || customer.customerName || 'N/A',
  //   email: customer.email || customer.customerEmail || 'N/A',
  //   phone: customer.mobileNumber || customer.customerMobileNumber || 'N/A',
  //   status: customer.status || 'Active'
  // })) || [];

  // Process merchant data
  // const merchantsData = merchantData?.data?.slice(0, 5)?.map((merchant: any, index: number) => ({
  //   id: index + 1,
  //   name: `${merchant.firstName || ''} ${merchant.lastName || ''}`.trim() || 'N/A',
  //   email: merchant.email || 'N/A',
  //   phone: merchant.mobileNo || 'N/A',
  //   status: merchant.isActive ? 'Active' : 'Inactive'
  // })) || [];

  const MetricCard = ({ title, value, icon: Icon, color }: any) => (
    <Card
      sx={{
        height: 160,
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${alpha(color, 0.3)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 28, color: color }} />
          </Box>
        </Box>
        <Box>
          <Typography variant="h3" component="div" sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: '2rem',
            lineHeight: 1.2
          }}>
            {value.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            opacity: 0.8,
            mb: 1
          }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{
            fontSize: '0.75rem',
            opacity: 0.6,
            lineHeight: 1.2
          }}>
            {title === t('dashboard.metrics.stores') && t('dashboard.metrics.storesDescription')}
            {title === t('dashboard.metrics.items') && t('dashboard.metrics.itemsDescription')}  
            {title === t('dashboard.metrics.orders') && t('dashboard.metrics.ordersDescription')}
            {title === t('dashboard.metrics.deliveredOrders') && t('dashboard.metrics.deliveredOrdersDescription')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const DataTable = ({ title, data, columns, showToggle = true, loading = false, onViewAll }: any) => (
    <Paper
      sx={{
        p: 0,
        height: '100%',
        background: '#ffffff',
        border: '1px solid #e8eaed',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}
    >
      <Box sx={{
        p: 3,
        borderBottom: '1px solid #e8eaed',
        background: 'linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: 'text.primary',
          fontSize: '1.125rem'
        }}>
          {title}
        </Typography>
      </Box>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafbfc' }}>
              {columns.map((column: any) => (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    borderBottom: '2px solid #e8eaed',
                    fontSize: '0.875rem',
                    py: 2
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.tables.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.tables.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row: any,) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafbfc'
                    }
                  }}
                >
                  {columns.map((column: any) => (
                    <TableCell key={column.id} sx={{ py: 2, fontSize: '0.875rem' }}>
                      {column.id === 'status' ? (
                        <Chip
                          label={row[column.id]}
                          size="small"
                          sx={{
                            backgroundColor: row[column.id] === 'Active' || "unblocked" ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24
                          }}
                        />
                      ) : (
                        row[column.id]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

    
      {showToggle && (
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid #e8eaed',
            background: '#fafbfc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={onViewAll}
            sx={{
              background: COLORS.primary.main,
              color: COLORS.secondary.main,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                background: COLORS.primary.dark,
                boxShadow: '0 4px 12px rgba(255, 71, 87, 0.3)',
              },
            }}
          >
            {t('dashboard.tables.viewAllOrders')}
          </Button>
        </Box>
      )}
    </Paper>
  );

  const handleViewAllOrders = () => {
    navigate('/layout/orderManagement');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)',
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Metrics Cards */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
            fontSize: '1.75rem'
          }}>
            {t('dashboard.title')}
          </Typography>
          {/* Filters: From / To date + Apply */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 3,
              mb: 3,
              flexWrap: 'wrap',
              p: 2,
              background: '#ffffff',
              border: '1px solid #e8eaed',
              borderRadius: 2,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{t('dashboard.filters.fromDate')}</Typography>
                <DatePicker
                  value={selectedFromDate}
                  onChange={(newValue) => setSelectedFromDate(newValue)}
                  format="MM/DD/YYYY"
                  maxDate={selectedToDate || dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 230 },
                      error: Boolean(selectedFromDate && selectedToDate && !isDateRangeValid),
                      helperText: selectedFromDate && selectedToDate && !isDateRangeValid ? t('dashboard.filters.dateRangeError') : undefined,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{t('dashboard.filters.toDate')}</Typography>
                <DatePicker
                  value={selectedToDate}
                  onChange={(newValue) => setSelectedToDate(newValue)}
                  format="MM/DD/YYYY"
                  minDate={selectedFromDate || undefined}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 230 },
                      error: Boolean(selectedFromDate && selectedToDate && !isDateRangeValid),
                      helperText: selectedFromDate && selectedToDate && !isDateRangeValid ? t('dashboard.filters.dateRangeErrorTo') : undefined,
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
            <Button
              variant="contained"
              size="medium"
              sx={{
                ml: 2,
                mb: 1,
                textTransform: 'none',
                px: 3,
              }}
              disabled={!isDateRangeValid}
              onClick={() => { if (isDateRangeValid) { refetchDashboard(); } }}
            >
              {t('dashboard.filters.apply')}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              sx={{
                ml: 1,
                mb: 1,
                textTransform: 'none',
                px: 3,
                color: COLORS.secondary.main,
                borderColor: COLORS.secondary.main
              }}
              onClick={handleResetDates}
            >
              {t('dashboard.filters.reset')}
            </Button>
          </Box>

          <Typography variant="body1" sx={{
            color: 'text.secondary',
            mb: 3,
            fontSize: '1rem',
            opacity: 0.8
          }}>
            {t('dashboard.metrics.subtitle')}
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 3
          }}>
            <MetricCard title={t('dashboard.metrics.stores')} value={Number((dashboardData as any)?.metricsCards?.storesCount) || 0} icon={StoreIcon} color="#9c27b0" />
            <MetricCard title={t('dashboard.metrics.items')} value={Number((dashboardData as any)?.metricsCards?.storesItemsCount) || 0} icon={DeliveryIcon} color="#d32f2f" />
            <MetricCard title={t('dashboard.metrics.orders')} value={Number((dashboardData as any)?.metricsCards?.ordersCount) || 0} icon={OrderIcon} color="#1976d2" />
            <MetricCard title={t('dashboard.metrics.deliveredOrders')} value={Number((dashboardData as any)?.metricsCards?.deliveredOrdersCount) || 0} icon={DeliveredIcon} color="#388e3c" />
          </Box>
        </Box>

        {/* Orders Chart */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: '#ffffff',
            border: '1px solid #e8eaed',
            borderRadius: 3,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            {t('dashboard.charts.orderDetails')}
          </Typography>
          <Box sx={{ height: 350 }}>
            {(() => {
              const dataset = (() => {
                const monthsOrder = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const apiSeries: any[] = ((dashboardData as any)?.orderTrendsLineChart || []) as any[];
                const byMonth = new Map<string, number>();
                apiSeries.forEach((item: any) => {
                  if (item?.month && typeof item?.totalOrders !== 'undefined') {
                    byMonth.set(String(item.month).slice(0,3), Number(item.totalOrders) || 0);
                  }
                });
                return monthsOrder.map((m) => ({ month: m, orders: byMonth.get(m) || 0 }));
              })();
              const hasOrders = dataset.some((d: any) => Number(d.orders) > 0);
              if (!hasOrders) {
                return (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{t('dashboard.charts.noData')}</Typography>
                  </Box>
                );
              }
              return (
                <LineChart
                  dataset={dataset}
                  xAxis={[{
                    scaleType: 'band',
                    dataKey: 'month',
                    tickLabelStyle: {
                      fontSize: 12,
                      fontWeight: 500,
                    }
                  }]}
                  yAxis={[{
                    min: 0,
                    tickLabelStyle: {
                      fontSize: 12,
                      fontWeight: 500,
                    }
                  }]}
                  series={[
                    {
                      dataKey: 'orders',
                      label: t('dashboard.charts.orders'),
                      color: '#1976d2',
                      curve: 'linear',
                      area: true,
                    },
                  ]}
                  height={300}
                  margin={{ top: 20, bottom: 30, left: 40, right: 20 }}
                  sx={{
                    '.MuiLineElement-root': {
                      strokeWidth: 3,
                      stroke: '#1976d2',
                    },
                    '.MuiMarkElement-root': {
                      stroke: '#1976d2',
                      strokeWidth: 2,
                      fill: '#ffffff',
                    },
                    '.MuiAreaElement-root': {
                      fill: 'url(#areaGradient)',
                    },
                  }}
                >
                  <defs>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#1976d2" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#1976d2" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                </LineChart>
              );
            })()}
          </Box>
        </Paper>

        {/* Charts Row - Side by Side */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            {t('dashboard.charts.analyticsOverview')}
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  {t('dashboard.charts.itemGraph')}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <ItemGraph
                    data={(() => {
                      const src: any[] = ((dashboardData as any)?.itemStatusPieChart || []) as any[];
                      return src.map((d: any) => ({
                        label: String(d.status).toLowerCase() === 'active' ? t('dashboard.charts.itemStatus.activeItems') : t('dashboard.charts.itemStatus.inactiveItems'),
                        value: Number(d.percentage) || 0,
                        color: String(d.status).toLowerCase() === 'active' ? '#27ae60' : '#c0392b',
                      }));
                    })()}
                  />
                </Box>
              </Paper>
            </Box>

            {/* <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Merchant Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <MerchantGraph />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Restaurant Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <RestaurantGraph />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Delivery Boy Graph
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <DeliveryBoyGraph />
                </Box>
              </Paper>
            </Box> */}
          </Box>
        </Box>

        {/* New Pie Charts Row */}
        {/* <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            Order & Revenue Analytics
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3
          }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Order Status Distribution
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <OrderStatusGraph />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
              <Paper
                sx={{
                  p: 3,
                  height: 400,
                  background: '#ffffff',
                  border: '1px solid #e8eaed',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: '1.125rem'
                }}>
                  Revenue by Category
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100% - 60px)'
                }}>
                  <RevenueGraph />
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box> */}

        {/* Tables Row - Side by Side */}
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            fontSize: '1.375rem'
          }}>
            {t('dashboard.charts.recentActivity')}
          </Typography>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}>
            <Box sx={{ width: '100%' }}>
              <DataTable
                title={t('dashboard.tables.orders')}
                data={(((dashboardData as any)?.recentOrdersList || []) as any[]).map((o: any, idx: number) => ({
                  id: idx + 1,
                  name: o.customerName || 'N/A',
                  item: o.itemQuantity ?? 'N/A',
                  amount: o.Amount ?? 'N/A',
                }))}
                loading={false}
                columns={[
                  { id: 'id', label: t('dashboard.tables.columns.sno') },
                  { id: 'name', label: t('dashboard.tables.columns.customerName') },
                  { id: 'item', label: t('dashboard.tables.columns.itemCount') },
                  { id: 'amount', label: t('dashboard.tables.columns.amount') },
                ]}
                onViewAll={handleViewAllOrders}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;


